import type { TokenGrant } from '@sentinel/shared';
import { authEndpoints } from '../api/auth-endpoints';
import { hostRole, hostUrl, type HostTarget } from './app-hosts';
import { challengeOf, newVerifier, takeVerifier } from './pkce';

/** One round trip per page: React runs effects twice in development, and a second start would
 *  cut the first navigation and replace its verifier; a second finish would find the verifier and
 *  the one-time code already spent. The second call gets the first one's answer. */
let starting: Promise<boolean> | null = null;
let finishing: { code: string; exchange: Promise<TokenGrant> } | null = null;

/** The start of the round trip on `console.` or `control.` (ADR 0013): a new verifier stays in
 *  this tab, its challenge goes to `accounts.` with the destination. False when this page is not
 *  one of the two apps. */
export function startRoundTrip(target: HostTarget = {}): Promise<boolean> {
  starting ??= goToAccounts(target);
  return starting;
}

async function goToAccounts(target: HostTarget): Promise<boolean> {
  const role = hostRole();
  if (role !== 'console' && role !== 'control') return false;
  const challenge = await challengeOf(newVerifier());
  const query = new URLSearchParams({
    to: role,
    challenge,
    ...(target.organizationId === undefined ? {} : { org: target.organizationId }),
    ...(target.grantId === undefined ? {} : { grant: target.grantId }),
  });
  window.location.assign(`${hostUrl('accounts')}/login?${query.toString()}`);
  return true;
}

/** Thrown when the tab lost its verifier or the code is missing: start again. */
export class RoundTripExpired extends Error {
  constructor() {
    super('handoff round trip expired');
  }
}

/** The end of the round trip: the code of `accounts.` and this tab's verifier make the app's own
 *  session. */
export function finishRoundTrip(code: string | undefined): Promise<TokenGrant> {
  if (code !== undefined && finishing?.code === code) return finishing.exchange;
  const verifier = takeVerifier();
  if (code === undefined || verifier === null) return Promise.reject(new RoundTripExpired());
  finishing = { code, exchange: authEndpoints.handoffExchange({ code, verifier }) };
  return finishing.exchange;
}
